import { Button, Form, Grid, Input, Space } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/** Person fields shared by the teacher and staff forms; the names match the API request fields. */
export interface PersonFormValues {
  lastName: string;
  firstName: string;
  middleName?: string;
  phone: string;
  additionalPhones: string[];
  email?: string;
}

export const emptyPerson: PersonFormValues = { lastName: "", firstName: "", middleName: "", phone: "", additionalPhones: [], email: "" };

/** The request body part built from the form: trimmed, blanks dropped, optional fields sent as null. */
export function personBody(values: PersonFormValues) {
  const middleName = values.middleName?.trim() ?? "";
  const email = values.email?.trim() ?? "";
  return {
    lastName: values.lastName.trim(),
    firstName: values.firstName.trim(),
    middleName: middleName.length > 0 ? middleName : null,
    phone: values.phone.trim(),
    additionalPhones: (values.additionalPhones ?? []).map((p) => p.trim()).filter((p) => p.length > 0),
    email: email.length > 0 ? email : null,
  };
}

/** Form items for names, phones and email; render inside an Ant Design Form whose values extend PersonFormValues. */
export function PersonFields() {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const required = { required: true, whitespace: true, message: t("person.validation.required") };
  const phoneRules = [{ required: true, message: t("person.validation.required") }, { pattern: /^[\d\s()+-]{9,20}$/, message: t("person.validation.phone") }];

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: screens.sm ? "1fr 1fr" : "1fr", gap: 12 }}>
        <Form.Item name="lastName" label={t("person.lastName")} rules={[required]}>
          <Input maxLength={100} autoComplete="family-name" />
        </Form.Item>
        <Form.Item name="firstName" label={t("person.firstName")} rules={[required]}>
          <Input maxLength={100} autoComplete="given-name" />
        </Form.Item>
      </div>
      <Form.Item name="middleName" label={t("person.middleName")}>
        <Input maxLength={100} autoComplete="additional-name" />
      </Form.Item>
      <Form.Item name="phone" label={t("person.phone")} rules={phoneRules}>
        <Input inputMode="tel" autoComplete="tel" placeholder={t("person.phoneHint")} />
      </Form.Item>
      <Form.List name="additionalPhones">
        {(fields, { add, remove }) => (
          <Form.Item label={t("person.additionalPhones")}>
            <Space orientation="vertical" style={{ width: "100%" }}>
              {fields.map((field) => (
                <div key={field.key} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <Form.Item name={field.name} rules={phoneRules} style={{ flex: 1, marginBottom: 0 }}>
                    <Input inputMode="tel" placeholder={t("person.phoneHint")} />
                  </Form.Item>
                  <Button type="text" danger icon={<DeleteOutlined />} aria-label={t("person.removePhone")} onClick={() => remove(field.name)} />
                </div>
              ))}
              {fields.length < 5 && (
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add("")} block>
                  {t("person.addPhone")}
                </Button>
              )}
            </Space>
          </Form.Item>
        )}
      </Form.List>
      <Form.Item name="email" label={t("person.email")} rules={[{ type: "email", message: t("person.validation.email") }]}>
        <Input maxLength={254} inputMode="email" autoComplete="email" />
      </Form.Item>
    </>
  );
}
